import { useEffect } from 'react';
import { Copy, Phone, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { trackReveal, ListingType } from '@/lib/tracking';

interface CallContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentName?: string;
  agencyName?: string;
  mobileNumber?: string;
  phoneNumber?: string;
  referenceId: string;
  listingId?: string;
  listingType?: ListingType;
}

export function CallContactDialog({
  open,
  onOpenChange,
  agentName = 'Property Agent',
  agencyName,
  mobileNumber,
  phoneNumber,
  referenceId,
  listingId,
  listingType,
}: CallContactDialogProps) {
  // Record a "phone reveal" the first time the dialog opens for this listing.
  useEffect(() => {
    if (open && listingId && listingType) {
      trackReveal({ listingType, listingId, revealType: 'phone' });
    }
  }, [open, listingId, listingType]);

  const copyNumber = (num: string) => {
    navigator.clipboard.writeText(num);
    toast.success('Number copied to clipboard');
  };

  const numbers = [
    { label: 'Mobile', value: mobileNumber },
    { label: 'Phone', value: phoneNumber },
  ].filter((n) => n.value);

  // If no numbers, show at least a placeholder
  if (numbers.length === 0 && mobileNumber) {
    numbers.push({ label: 'Mobile', value: mobileNumber });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-lg font-bold">Contact Us</DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-2 text-center">
          <div className="font-semibold text-base">{agentName}</div>
          {agencyName && (
            <div className="text-sm text-muted-foreground">{agencyName}</div>
          )}
        </div>

        <Separator />

        <div className="p-5 space-y-3">
          {numbers.map((n, i) => (
            <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="text-xs text-muted-foreground font-medium">{n.label}</div>
                <a
                  href={`tel:${n.value}`}
                  className="text-primary font-semibold text-lg hover:underline"
                >
                  {n.value}
                </a>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-primary"
                onClick={() => copyNumber(n.value!)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <Separator />

        <div className="p-5 text-center">
          <p className="text-xs text-muted-foreground">
            Please quote property reference{' '}
            <span className="font-semibold text-foreground">
              EthioRealEstates - {referenceId}
            </span>{' '}
            when calling.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
